/**
 * 授权列表表格（P-10）。
 *
 * 三条来自规范、且都不是「好看」而是「防错」的处理：
 * - 「设备 3/5」达上限时数字变 `--st-hold` 并追加 `circle-alert`（§5 P-10）；
 * - 到期 < 7 天用相对时间，其余用绝对日期（相对时间会让人失去可核对的锚点）；
 * - 只有建立了索引的列可排序，其余列头 `cursor: default` 且给出 tooltip 说明原因（§6.4）。
 */
import { Link } from 'react-router-dom';
import { Icon } from '../../icons/Icon';
import { StatusBadge } from '../../components/StatusBadge';
import { CopyButton } from '../../components/CopyButton';
import { ICON_SIZE } from '../../tokens';
import { formatDateTime, formatExpiry, middleTruncate } from '../../lib/format';
import type { Customer, License, Product } from '../../api/types';
import type { LicenseFilters } from './useLicenseQuery';

type SortKey = LicenseFilters['sort'];

interface Column {
  readonly key: string;
  readonly label: string;
  readonly sortBy?: SortKey;
  readonly numeric?: boolean;
  /** 不可排序的原因，进 tooltip —— 诚实地把数据库约束暴露给运营者 */
  readonly unsortableReason?: string;
}

const COLUMNS: readonly Column[] = [
  { key: 'id', label: 'license_id', unsortableReason: '该列未建立索引，暂不支持排序' },
  { key: 'product', label: '产品', unsortableReason: '该列未建立索引，暂不支持排序' },
  { key: 'plan', label: '套餐', unsortableReason: '该列未建立索引，暂不支持排序' },
  { key: 'status', label: '状态', unsortableReason: '状态请用上方筛选 chips' },
  { key: 'devices', label: '设备', numeric: true, unsortableReason: '该列未建立索引，暂不支持排序' },
  { key: 'expires', label: '到期时间', sortBy: 'expires_at' },
  { key: 'customer', label: '客户', unsortableReason: '该列未建立索引，暂不支持排序' },
  { key: 'updated', label: '最近更新', sortBy: 'updated_at' },
  { key: 'actions', label: '操作' },
];

export interface LicenseTableProps {
  readonly rows: readonly License[];
  readonly products: readonly Product[];
  readonly customers: readonly Customer[];
  readonly filters: LicenseFilters;
  readonly onSort: (sort: SortKey) => void;
}

export function LicenseTableHead({
  filters,
  onSort,
}: Pick<LicenseTableProps, 'filters' | 'onSort'>) {
  return (
    <thead>
      <tr>
        {COLUMNS.map((column) => {
          const sortable = column.sortBy !== undefined;
          const isActive = sortable && filters.sort === column.sortBy;
          return (
            <th
              key={column.key}
              scope="col"
              className={column.numeric === true ? 'cell--numeric' : undefined}
              aria-sort={isActive ? (filters.order === 'asc' ? 'ascending' : 'descending') : 'none'}
              title={column.unsortableReason}
              style={{ cursor: sortable ? 'pointer' : 'default' }}
            >
              {sortable ? (
                <button
                  type="button"
                  className="link-button"
                  style={{ color: 'inherit', textDecoration: 'none', font: 'inherit' }}
                  onClick={() => onSort(column.sortBy as SortKey)}
                >
                  <span className="cell-inline">
                    {column.label}
                    {isActive ? (
                      <Icon
                        name={filters.order === 'asc' ? 'arrow-up' : 'arrow-down'}
                        size={ICON_SIZE.badge}
                      />
                    ) : null}
                  </span>
                </button>
              ) : (
                column.label
              )}
            </th>
          );
        })}
      </tr>
    </thead>
  );
}

export const LICENSE_COLUMN_COUNT = COLUMNS.length;

export function LicenseTableBody({ rows, products, customers }: Omit<LicenseTableProps, 'filters' | 'onSort'>) {
  const productName = (id: string) => products.find((p) => p.id === id)?.name ?? middleTruncate(id);
  const customerLabel = (id: string | null) => {
    if (id === null) return null;
    const customer = customers.find((c) => c.id === id);
    return customer?.name ?? customer?.email ?? middleTruncate(id);
  };

  return (
    <tbody>
      {rows.map((license) => {
        // 防御性守卫：plan_snapshot 是兑换时刻的套餐快照，本应为必填对象。
        // 若因契约漂移缺失，绝不能让它抛错导致整页白屏（admin 全局无 ErrorBoundary），
        // 而是把缺失落在可见单元格上——行永远渲染，问题可见可追溯（对齐 ProductsPage 范式）。
        const snap = license.plan_snapshot ?? null;
        const max = snap?.max_devices ?? 0;
        const atLimit = snap !== null && license.activated_device_count >= max;
        const expiry = formatExpiry(license.expires_at);
        const customer = customerLabel(license.customer_id);

        return (
          <tr key={license.id} tabIndex={0} data-license-row="true">
            <td>
              <span className="cell-inline">
                <span className="cell-id" title={license.id}>
                  {middleTruncate(license.id, 10, 4)}
                </span>
                <CopyButton value={license.id} what=" license_id" />
              </span>
            </td>

            <td>{productName(license.product_id)}</td>

            <td>
              {snap?.plan_name ??
                <span className="cell-muted" title="套餐快照缺失（契约漂移）">快照缺失</span>}
            </td>

            <td>
              <StatusBadge status={license.status} reason={license.status_reason} />
            </td>

            <td className="cell--numeric">
              {snap === null ? (
                <span className="cell-muted">—</span>
              ) : (
                <span className={atLimit ? 'cell-inline cell-warn' : 'cell-inline'}>
                  {atLimit ? <Icon name="circle-alert" size={ICON_SIZE.badge} /> : null}
                  {license.activated_device_count} / {max}
                </span>
              )}
            </td>

            <td>
              <span className={expiry.urgent ? 'cell-urgent' : undefined} title={expiry.title}>
                {expiry.text}
              </span>
            </td>

            <td>
              {customer === null ? (
                <span className="cell-muted">未登记</span>
              ) : (
                <span title={customer}>{customer}</span>
              )}
            </td>

            <td title={formatDateTime(license.updated_at)}>{formatDateTime(license.updated_at)}</td>

            <td className="cell--actions">
              <span className="row-actions">
                <Link
                  to={`/licenses/${license.id}`}
                  className="btn btn--quiet btn--icon"
                  aria-label={`查看授权 ${license.id} 的详情`}
                  title="查看详情"
                >
                  <Icon name="chevron-right" size={ICON_SIZE.inline} />
                </Link>
              </span>
            </td>
          </tr>
        );
      })}
    </tbody>
  );
}
